import { AxiosResponse } from 'axios'
import request from '@/helpers/request'
import { DealDetails } from '@/classes/DealDetails'
import { FrontendGiveawayParticipantInfo, GiveawayEntry, GiveawayInfo } from '@/classes/Giveaways'
import { Article } from '@/classes/Articles'
import { ScrapeAmazonSearchResultsResult } from '@/classes/Search'
import TrainingDataItem from '@/classes/TrainingDataItem'
import {
    AccountInfo,
    GroupInfo,
    CreateAccountRequest,
    CreateAccountResponse,
    QRCodeResponse,
    AccountStatusResponse,
    AddToQueueRequest,
    QueueStatusResponse,
    EngineStatusResponse,
    StartEngineResponse,
    StopEngineResponse,
    UploadFileResponse,
    WhatsAppConfiguration,
    GetAllStatusResponse,
    QueueProcessingStatusResponse
} from '@/classes/WhatsApp'

export default {
    getArticles: (pageSize: number, pageNumber: number, excludeArticleId: number, isGiftList: boolean, searchText?: string): Promise<AxiosResponse<Article[]>> => {
        const searchParam = searchText ? `&searchText=${encodeURIComponent(searchText)}` : '';
        // Explicitly pass isActive=true for public-facing requests to show only active articles
        return request.get(`/GetArticles?pageSize=${pageSize}&pageNumber=${pageNumber}&excludeArticleId=${excludeArticleId}&isGiftList=${isGiftList}&isActive=true${searchParam}`);
    },
    getArticleDetails: (articleId: number, includeDeals: boolean): Promise<AxiosResponse<Article>> => request.get(`/GetArticleDetails?articleId=${articleId}&includeDeals=${includeDeals}`),
    getLatestDeals: (pageSize: number, pageNumber: number, excludeDeal: number): Promise<AxiosResponse<DealDetails[]>> => { 
        if(excludeDeal) {
            return request.get(`/GetLatestDeals?pageSize=${pageSize}&pageNumber=${pageNumber}&excludeDeal=${excludeDeal}`);
        }
        return request.get(`/GetLatestDeals?pageSize=${pageSize}&pageNumber=${pageNumber}`);
    },
    submitContactUsForm: (form: any) => request.post('/SubmitContactUsForm', form),
    newsletterSignup: (form: any) => request.post('/NewsletterSignup', form),
    getDealsByDate: (pageSize: number, pageNumber: number, excludeDeal: number, date: string): Promise<AxiosResponse<DealDetails[]>> => request.get(`/GetDealsByDate?pageSize=${pageSize}&pageNumber=${pageNumber}&excludeDeal=${excludeDeal}&date=${date}`),
    getDeal: (dealId: number): Promise<AxiosResponse<DealDetails>> => request.get(`/GetDeal?dealId=${dealId}`),
    getGiveawayInfo: (ParticipantHash: string | null = null): Promise<AxiosResponse<GiveawayInfo>> => request.get(`/GetGiveawayInfo?ParticipantHash=${ParticipantHash}`),
    newGiveawayEntry: (newEntry: GiveawayEntry): Promise<AxiosResponse<FrontendGiveawayParticipantInfo>> => request.post('/NewGiveawayEntry', newEntry),

    ////////
    postToFacebookPage: (data: any) => request.post('/PostToFacebookPage', data),
    postToFacebookGroup: (data: any) => request.post('/PostToFacebookGroup', data),
    postToTwitter: (data: any) => request.post('/PostToTwitter', data),
    postToTwitter2: (data: any) => request.post('/PostToTwitter', data),
    postToTelegram: (data: any) => request.post('/PostToTelegram', data),
    getJCNAd: (zid: any, pid: any) => request.getExternal(`https://servedby.jewishcontentnetwork.com/servlet/view/banner/url/zone?zid=${zid}&pid=${pid}`),
    googleAccessTokens: () => request.get('/googleAccessTokens'),
    googleGetRealTimeData: (params: string, headers: any) => request.getExternal(`https://www.googleapis.com/analytics/v3/data/realtime?${params}`, headers),
    analysis: {
        searchAnalytics: (data: any) => request.post('/analysis/analytics', data),
        searchOrderHistory: (data: any) => request.post('/analysis/orderHistory', data)
    },

    getBlogHomepage: (): Promise<AxiosResponse<any>> => request.get('/BlogHomepage'),
    getBlogPosts: (): Promise<AxiosResponse<any>> => request.get('/BlogPosts'),
    getBlogPost: (slug: string): Promise<AxiosResponse<any>> => request.get(`/BlogPost?slug=${slug}`),
    getSearchSuggestions: (searchQuery: string): Promise<AxiosResponse<any>> => request.get(`/search-suggestions?query=${searchQuery}`),
    getSearchResults: (searchQuery: string, maxPrice: number, minPercentOff: number, nodes: string, outletItemId?: string): Promise<AxiosResponse<ScrapeAmazonSearchResultsResult>> => {
        const params = new URLSearchParams({
            query: searchQuery,
            price: maxPrice?.toString() || '',
            percent: minPercentOff?.toString() || '',
            nodes: nodes || ''
        });
        if (outletItemId) {
            params.append('outletItemId', outletItemId);
        }
        return request.get(`/search-results?${params.toString()}`);
    },
    getOutletItems: (): Promise<AxiosResponse<any>> => request.get('/GetOutletItems'),
    
    getPosts: (pageSize: number, searchText: string, status: string): Promise<AxiosResponse<any>> => request.get(`/GetPosts?pageSize=${pageSize}&searchText=${searchText}&status=${status}`),

    // Single-row lookup for in-place grid updates after a tuner modal closes.
    // Bypasses every other filter -- pure point-lookup by deal id.
    getPostsDealsRow: (dealId: number): Promise<AxiosResponse<any>> =>
        request.get(`/GetPostsDeals?pageSize=1&pageNumber=1&dealId=${dealId}&sort=CreatedAt:desc&searchText=&stateFilter=&includeArchived=true`),

    getPostsDeals: (params: {
        pageSize: number; pageNumber: number; searchText: string;
        stateFilter: string; sort: string;
        categoryId?: number | null;
        brandFilter?: string; promoFilter?: string; sourceFilter?: string;
        retailerFilter?: string; gradeFilter?: string; typeFilter?: number | null;
        sellerFilter?: string;
        postIdFilter?: string;
        listMin?: number | null; listMax?: number | null;
        saleMin?: number | null; saleMax?: number | null;
        finalMin?: number | null; finalMax?: number | null;
        pctOffMin?: number | null; pctOffMax?: number | null;
        stockMin?: number | null; stockMax?: number | null;
        rankMin?: number | null; rankMax?: number | null;
        audienceMin?: number | null; audienceMax?: number | null;
        includeArchived?: boolean;
    }): Promise<AxiosResponse<any>> => {
        const qs = new URLSearchParams()
        qs.set('pageSize', String(params.pageSize))
        qs.set('pageNumber', String(params.pageNumber))
        qs.set('searchText', params.searchText || '')
        qs.set('stateFilter', params.stateFilter || '')
        qs.set('sort', params.sort)
        if (params.categoryId) qs.set('categoryId', String(params.categoryId))
        if (params.brandFilter) qs.set('brandFilter', params.brandFilter)
        if (params.promoFilter) qs.set('promoFilter', params.promoFilter)
        if (params.sourceFilter) qs.set('sourceFilter', params.sourceFilter)
        if (params.retailerFilter) qs.set('retailerFilter', params.retailerFilter)
        if (params.gradeFilter) qs.set('gradeFilter', params.gradeFilter)
        if (params.typeFilter != null) qs.set('typeFilter', String(params.typeFilter))
        if (params.sellerFilter) qs.set('sellerFilter', params.sellerFilter)
        if (params.postIdFilter) qs.set('postIdFilter', params.postIdFilter)
        const setNum = (k: string, v: number | null | undefined) => { if (v != null && !Number.isNaN(v)) qs.set(k, String(v)) }
        setNum('listMin', params.listMin);   setNum('listMax', params.listMax)
        setNum('saleMin', params.saleMin);   setNum('saleMax', params.saleMax)
        setNum('finalMin', params.finalMin); setNum('finalMax', params.finalMax)
        setNum('pctOffMin', params.pctOffMin); setNum('pctOffMax', params.pctOffMax)
        setNum('stockMin', params.stockMin); setNum('stockMax', params.stockMax)
        setNum('rankMin', params.rankMin);   setNum('rankMax', params.rankMax)
        setNum('audienceMin', params.audienceMin); setNum('audienceMax', params.audienceMax)
        if (params.includeArchived) qs.set('includeArchived', 'true')
        return request.get(`/GetPostsDeals?${qs.toString()}`)
    },
    getCategories: (): Promise<AxiosResponse<any>> => request.get('/GetCategories'),
    getDealJson: (dealId: number): Promise<AxiosResponse<any>> => request.get(`/GetDealJson?dealId=${dealId}`),
    processPostToDeal: (postText: string): Promise<AxiosResponse<any>> => request.post('/ProcessPostToDeal', { postText }),
    getNextTunerDeal: (dealId?: number): Promise<AxiosResponse<any>> =>
        request.get(dealId ? `/GetNextTunerDeal?dealId=${dealId}` : '/GetNextTunerDeal'),
    saveTunerLabel: (payload: {
        dealId: number;
        yourTier: string;
        confidence?: number;
        notes?: string;
        priceNotes?: string;
        realRetailPrice?: number;
        realSalePrice?: number;
        realFinalPrice?: number;
        greatThreshold?: number;
        goodThreshold?: number;
        saleThreshold?: number;
    }): Promise<AxiosResponse<any>> => request.post('/SaveTunerLabel', payload),
    getTunerStats: (): Promise<AxiosResponse<any>> => request.get('/GetTunerStats'),
    getAudienceTunerBatch: (nicheId: string = 'mommydeals', batchSize: number = 10): Promise<AxiosResponse<any>> =>
        request.get(`/GetAudienceTunerBatch?nicheId=${nicheId}&batchSize=${batchSize}`),
    getAudienceTunerDeal: (dealId: number, nicheId: string = 'mommydeals'): Promise<AxiosResponse<any>> =>
        request.get(`/GetAudienceTunerDeal?dealId=${dealId}&nicheId=${nicheId}`),
    saveAudienceLabel: (payload: { dealId: number; nicheId?: string; yourScore: number; notes?: string }): Promise<AxiosResponse<any>> =>
        request.post('/SaveAudienceLabel', payload),
    saveAudienceLabelBulk: (payload: { dealIds: number[]; nicheId?: string; yourScore: number; notes?: string }): Promise<AxiosResponse<any>> =>
        request.post('/SaveAudienceLabelBulk', payload),

    // Pairwise preference labeling -- parallel track, doesn't share
    // state with the absolute-scoring AudienceTuner endpoints. Backs
    // /audience-pair-tuner.
    getAudiencePairBatch: (nicheId: string = 'mommydeals', batchSize: number = 10): Promise<AxiosResponse<any>> =>
        request.get(`/GetAudiencePairBatch?nicheId=${nicheId}&batchSize=${batchSize}`),
    saveAudiencePairJudgment: (payload: {
        dealIdA: number; dealIdB: number;
        winner: 'A' | 'B' | 'T' | 'N';
        nicheId?: string;
        modelScoreA?: number | null; modelScoreB?: number | null;
        modelVersionAtPair?: string | null;
        notes?: string;
    }): Promise<AxiosResponse<any>> =>
        request.post('/SaveAudiencePairJudgment', payload),
    getAudiencePairTunerStats: (nicheId: string = 'mommydeals'): Promise<AxiosResponse<any>> =>
        request.get(`/GetAudiencePairTunerStats?nicheId=${nicheId}`),

    // Manual model retraining triggers + status. Backs the "Train Now" banner
    // on the tuner pages. PricePy runs the actual work in a daemon thread, so
    // these calls return immediately.
    getTrainingStatus: (): Promise<AxiosResponse<any>> => request.get('/training/status'),
    trainPriceModel: (): Promise<AxiosResponse<any>> => request.post('/training/price'),
    trainNicheModel: (): Promise<AxiosResponse<any>> => request.post('/training/niche'),
    trainLabelModel: (): Promise<AxiosResponse<any>> => request.post('/training/label'),
    trainPairModel: (): Promise<AxiosResponse<any>> => request.post('/training/pair'),
    getAudienceTunerStats: (nicheId: string = 'mommydeals'): Promise<AxiosResponse<any>> =>
        request.get(`/GetAudienceTunerStats?nicheId=${nicheId}`),

    // Admin: TelegramClient watchlist (dbo.WatchedChannels). Backs /watched-channels.
    getWatchedChannels: (): Promise<AxiosResponse<any>> => request.get('/watched-channels'),
    addWatchedChannel: (payload: { peerId?: number; username?: string; note?: string | null }): Promise<AxiosResponse<any>> =>
        request.post('/watched-channels', payload),
    updateWatchedChannel: (id: number, payload: { isActive?: boolean; note?: string | null }): Promise<AxiosResponse<any>> =>
        request.patch(`/watched-channels/${id}`, payload),
    deleteWatchedChannel: (id: number): Promise<AxiosResponse<any>> =>
        request.delete(`/watched-channels/${id}`),

    // Public faceted deal search — backs the /deals customer-facing page.
    // Pass any subset of params; the controller treats unset filters as "no filter".
    searchDeals: (params: Record<string, string | number | boolean | undefined | null>): Promise<AxiosResponse<any>> => {
        const qs = Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
            .join('&')
        return request.get(`/deals/search${qs ? '?' + qs : ''}`)
    },
    headerRequest: (url: string) => request.headRequest(url),

    ////////
    getModels: (): Promise<AxiosResponse<any>> => request.get('/aitrain/get-models'),
    guess: (modelName: string, text: string): Promise<AxiosResponse<any>> => request.post('/aitrain/execute', { modelName, text }),
    trainSingle: (trainingDataItem: TrainingDataItem): Promise<AxiosResponse<any>> => request.post('/aitrain/train-single', trainingDataItem),

    whatsapp: {
        getAccounts: (): Promise<AxiosResponse<AccountInfo[]>> => request.get('/whatsapp/accounts'),
        getGroups: (accountId: string): Promise<AxiosResponse<GroupInfo[]>> => request.get(`/whatsapp/groups/${accountId}`),
        addToQueue: (data: AddToQueueRequest): Promise<AxiosResponse<{ success: boolean }>> => request.post('/whatsapp/queue/add', data),
        getQueueStatus: (): Promise<AxiosResponse<QueueStatusResponse>> => request.get('/whatsapp/queue/status'),
        clearMessages: (status: string): Promise<AxiosResponse<{ success: boolean; count: number }>> => request.post(`/whatsapp/messages/clear?status=${status}`),
        clearQueue: (): Promise<AxiosResponse<{ success: boolean; count: number }>> => request.post('/whatsapp/queue/clear'),
        clearScheduledMessages: (): Promise<AxiosResponse<{ success: boolean; count: number }>> => request.post('/whatsapp/queue/scheduled/clear'),
        startEngine: (): Promise<AxiosResponse<StartEngineResponse>> => request.post('/whatsapp/engine/start'),
        stopEngine: (): Promise<AxiosResponse<StopEngineResponse>> => request.post('/whatsapp/engine/stop'),
        getEngineStatus: (): Promise<AxiosResponse<EngineStatusResponse>> => request.get('/whatsapp/engine/status'),
        startQueue: (): Promise<AxiosResponse<{ success: boolean; queueProcessingEnabled: boolean }>> => request.post('/whatsapp/queue/start'),
        stopQueue: (): Promise<AxiosResponse<{ success: boolean; queueProcessingEnabled: boolean }>> => request.post('/whatsapp/queue/stop'),
        getQueueProcessingStatus: (): Promise<AxiosResponse<QueueProcessingStatusResponse>> => request.get('/whatsapp/queue/processing-status'),
        uploadFile: (formData: FormData): Promise<AxiosResponse<UploadFileResponse>> => request.post('/whatsapp/upload', formData),
        createAccount: (accountId: string): Promise<AxiosResponse<CreateAccountResponse>> => request.post('/whatsapp/accounts/create', { accountId } as CreateAccountRequest),
        getQRCode: (accountId: string): Promise<AxiosResponse<QRCodeResponse>> => request.get(`/whatsapp/accounts/${accountId}/qr`),
        getAccountStatus: (accountId: string): Promise<AxiosResponse<AccountStatusResponse>> => request.get(`/whatsapp/accounts/${accountId}/status`),
        disconnectAccount: (accountId: string): Promise<AxiosResponse<{ success: boolean }>> => request.post(`/whatsapp/accounts/${accountId}/disconnect`),
        deleteAccount: (accountId: string): Promise<AxiosResponse<{ success: boolean }>> => request.post(`/whatsapp/accounts/${accountId}/delete`),
        getConfigurations: (): Promise<AxiosResponse<WhatsAppConfiguration[]>> => request.get('/whatsapp/configurations'),
        getConfiguration: (id: string): Promise<AxiosResponse<any>> => request.get(`/whatsapp/configurations/${id}`),
        saveConfiguration: (config: any): Promise<AxiosResponse<{ success: boolean }>> => request.post('/whatsapp/configurations', config),
        deleteConfiguration: (id: string): Promise<AxiosResponse<{ success: boolean }>> => request.post(`/whatsapp/configurations/${id}/delete`),
        resetConfiguration: (id: string): Promise<AxiosResponse<any>> => request.post(`/whatsapp/configurations/${id}/reset`),
        removeFromQueue: (accountId: string, id: string): Promise<AxiosResponse<{ success: boolean }>> => request.post(`/whatsapp/queue/remove/${accountId}/${id}`),
        // Unified endpoint - get all messages, optionally filtered by status
        getMessages: (status?: string): Promise<AxiosResponse<QueueStatusResponse>> => {
            const url = status ? `/whatsapp/messages?status=${status}` : '/whatsapp/messages';
            return request.get(url);
        },
        // Consolidated endpoint - get all statuses in one call
        getAllStatus: (): Promise<AxiosResponse<GetAllStatusResponse>> => request.get('/whatsapp/status/all'),
        reAddToQueue: (accountId: string, id: string): Promise<AxiosResponse<{ success: boolean }>> => request.post(`/whatsapp/messages/${accountId}/${id}/re-add-to-queue`),
        postScheduledMessageNow: (accountId: string, id: string): Promise<AxiosResponse<{ success: boolean }>> => request.post(`/whatsapp/messages/${accountId}/${id}/post-now`),
        checkMessageStatus: (accountId: string, id: string): Promise<AxiosResponse<{ success: boolean; status?: string; message?: string; errorMessage?: string }>> => request.post(`/whatsapp/messages/${accountId}/${id}/check-status`),
        updateScheduledMessageTime: (accountId: string, id: string, scheduledAt: string): Promise<AxiosResponse<{ success: boolean }>> => request.put(`/whatsapp/messages/${accountId}/${id}/scheduled-time`, { scheduledAt }),
    },

    getErrorLogFiles: (): Promise<AxiosResponse<{ files: Array<{ fileName: string; fullPath: string; createdDate: string; fileSize: number }> }>> => {
        return request.get('/GetErrorLogFiles');
    },
    getErrorLogFileContent: (fileName: string): Promise<AxiosResponse<{ content: string; fileName: string }>> => {
        return request.get(`/GetErrorLogFileContent?fileName=${encodeURIComponent(fileName)}`);
    },
    deleteErrorLogFile: (fileName: string): Promise<AxiosResponse<{ success: boolean }>> => {
        return request.post(`/DeleteErrorLogFile?fileName=${encodeURIComponent(fileName)}`);
    },

    scoring: {
        classifyPost: (postText: string): Promise<AxiosResponse<any>> => request.post('/scoring/classify-post', { postText }),
        extractSpacy: (postText: string, modelName?: string): Promise<AxiosResponse<any>> => request.post('/scoring/extract-spacy', { postText, modelName }),
        extractGliner: (postText: string, labels?: string[]): Promise<AxiosResponse<any>> => request.post('/scoring/extract-gliner', { postText, labels }),
        resolveUrl: (url: string): Promise<AxiosResponse<any>> => request.post('/scoring/resolve-url', { url }),
        ingestDeal: (deal: any): Promise<AxiosResponse<any>> => request.post('/scoring/ingest', deal),
        normalizeDeal: (deal: any): Promise<AxiosResponse<any>> => request.post('/scoring/normalize', deal),
    }
}